(ns io.daskul.webapp.widgets.conversation.linked-leads
  "Pure view-model logic for the conversation sidebar's linked-leads list
   (SMS-35 Phase 1 — scannable list of linked leads).

   Carries NO UI dependency (no `io.daskul.webapp.ui` / `@tabler` import) so it
   runs under the webapp-ui :node-test target — the JSX render lives in the
   tutorial conversation-thread view. Mirrors the SMS-106 advanced-fields split:
   pure row-building/ordering logic is node-tested, the visual layout is
   e2e-verified (frontend-component-architecture — separate logic from layout).

   Slice 1 covers the label fallback, the trial line, and active-before-lost
   ordering. Program name is layered on in Slice 2 (see `program-name` +
   `meta-line`)."
  (:require [clojure.string :as str]))

(defn- blank->nil [s]
  (when (and s (not (str/blank? s))) s))

(defn for-conversation
  "The cached linked-leads result, but only when it belongs to `conversation-id`.

   The fetch lands in one global db slot, so between opening thread B and its
   response arriving the slot still holds thread A's leads — harmless while only
   the aside read it, but a header chip reading it unfiltered would state that
   A's lead belongs to B (#760).

   nil means \"nothing loaded for this conversation yet\" (render nothing);
   an empty vector means \"loaded, and there are none\" (render the 'No lead yet'
   marker). Collapsing the two would flash that marker on every thread open."
  [cached conversation-id]
  (when (and (some? conversation-id)
             (= (:conversation-id cached) conversation-id))
    (vec (:items cached))))

(defn student-name
  "Student full name from first/last, or nil when neither is present.
   The student name is what distinguishes sibling leads (one guardian, several
   children) on a single conversation thread."
  [{:keys [student-first-name student-last-name]}]
  (blank->nil (str/trim (str student-first-name " " student-last-name))))

(defn primary-label
  "The row's primary label. Prefers the student name (distinguishes siblings),
   then the server-computed display-name, then social-media-name, then the
   guardian's first/last, and finally a literal so a row is never blank."
  [{:keys [display-name social-media-name first-name last-name] :as lead}]
  (or (student-name lead)
      (blank->nil display-name)
      (blank->nil social-media-name)
      (blank->nil (str/trim (str first-name " " last-name)))
      "Unnamed lead"))

(defn lost?
  "A lost lead is de-emphasized and sorted last."
  [{:keys [state]}]
  (= "lost" state))

(def ^:private terminal-states
  "Closed leads. Mirrors the API's own `terminal-lead-states`
   (leads/after_read.clj) — the states that get no next action and no trial
   enrichment."
  #{"enrolled" "lost"})

(defn terminal?
  "A closed lead: enrolled (converted) or lost. Broader than `lost?`, which is
   only about de-emphasis in the aside."
  [{:keys [state]}]
  (contains? terminal-states state))

(defn order-rows
  "Active leads first, lost leads last. Order within each group is preserved
   (the server already sorts by nearest trial), so this only sinks lost leads.
   ClojureScript's sort is stable."
  [leads]
  (sort-by #(if (lost? %) 1 0) leads))

(defn state-ui
  "The server's FSM view-data for this lead's current state — the human label
   (\"Trial Scheduled\", not \"trial-scheduled\") and the badge tone. Every list
   item carries it under [:_meta :fsm :state]; nil when the item was not
   FSM-enriched, and the badge then falls back to the raw state string."
  [lead]
  (get-in lead [:_meta :fsm :state]))

;; ============================================================================
;; Thread header chip (#760)
;; ============================================================================

(defn header-chip
  "View-model for the conversation thread header's lead indicator.

   The chip answers \"where is this inquiry at\", so it carries the lead's state
   and the server-computed `:card-subtitle` — the same next-action string the
   Pipeline's column reads (#324), so the two surfaces cannot disagree. \"1 lead\"
   would say almost nothing; \"Trial tomorrow, 3:00 PM\" says what to do.

   `leads` is `ll/for-conversation`'s output, so nil (not loaded for this thread
   yet) yields no chip — rendering \"No lead yet\" before the fetch lands would
   invite a duplicate on every thread open. Kinds:

     :none  no lead → the marker that opens the create-or-link menu
     :one   the lead's state + next action → tap through to its detail page
     :many  a count → tap opens the leads list, because picking which sibling
            this conversation is about is a choice, not something to guess

   `suggestions` (ll/suggestions output, optional) contributes :match-count and
   :match-label at EVERY kind, saying only how many — the chip is one line on a
   375px header, so the names belong in the menu it opens. It exists because on
   mobile the aside lives behind a drawer: without a count here the suggestion
   panel is invisible on the surface where most triage happens, and the visible
   path leads straight to Create a new lead — the duplicate this all exists to
   stop.

   Every kind, not just :none, because the two classifications differ on
   purpose. This counts ANY lead, so a thread whose only lead is lost is :one
   and says `Lost` — which is the true answer to \"where is this at\". The
   server counts only OPEN leads, so it calls that same thread unattached and
   suggests for it. Both are right; hanging the badge off :none alone meant the
   one case where they disagree had no route to its own suggestions."
  ([leads] (header-chip leads nil))
  ([leads suggestions]
   (when (some? leads)
     (let [n (count leads)
           matches (when suggestions
                     (+ (count (:rows suggestions)) (or (:more suggestions) 0)))
           ;; Kept separate from :label so the chip renders it as a BADGE.
           ;; Appended to the label it was one more run of dimmed 12px text
           ;; beside another, which is how you say something and have nobody
           ;; read it. "possible matches" rather than "matches" because the
           ;; whole contract here is that these are guesses to confirm.
           match-label (when (and matches (pos? matches))
                         (str matches (if (= 1 matches)
                                        " possible match"
                                        " possible matches")))
           with-matches #(assoc % :match-count (or matches 0)
                                :match-label match-label)]
       (case n
         0 (with-matches {:kind :none :label "No lead yet"})
         1 (let [lead (first leads)]
             (with-matches
               {:kind :one
                :lead-id (:id lead)
                :label (primary-label lead)
                :state (:state lead)
                :state-ui (state-ui lead)
                :subtitle (blank->nil (:card-subtitle lead))}))
         (with-matches {:kind :many :count n :label (str n " leads")}))))))

;; ============================================================================
;; Create Lead slide-over notice (#760)
;; ============================================================================

(def ^:private max-notice-rows
  "Enough to recognise a sibling set; past that the count carries it. The
   slide-over's job is capturing a lead, not browsing the ones that exist."
  3)

(defn existing-notice
  "View-model for the Create Lead slide-over's \"this conversation already has a
   lead\" notice.

   A sign, not a gate: Create stays enabled and there is no confirm dialog.
   Siblings and family are a legitimate multi-lead path, and a dialog on a
   legitimate path only teaches people to click through it.

   The tone splits on whether anything is still in play. An active lead means
   this is probably the duplicate the issue is about; when every existing lead
   is closed (lost, or enrolled), a new inquiry from the same guardian usually
   IS a new lead, so the line informs rather than warns.

   nil when there is nothing to say."
  [leads]
  (when (seq leads)
    (let [rows (order-rows leads)
          n (count rows)
          any-active? (boolean (some (complement terminal?) rows))]
      {:tone (if any-active? :warning :muted)
       :count n
       :headline (if any-active?
                   (if (= 1 n)
                     "This conversation already has a lead."
                     (str "This conversation already has " n " leads."))
                   (if (= 1 n)
                     "This conversation has a closed lead."
                     (str "This conversation has " n " closed leads.")))
       :rows (vec (take max-notice-rows rows))
       :more (max 0 (- n max-notice-rows))})))

(def ^:private month-abbrev
  ["Jan" "Feb" "Mar" "Apr" "May" "Jun" "Jul" "Aug" "Sep" "Oct" "Nov" "Dec"])

(defn- format-session-when
  "Render a session's wall clock as 'Jun 20, 1:00 PM' from its naive `date`
   ('YYYY-MM-DD') + `start-time` ('HH:MM:SS'), or nil. No tz — the value is
   already the school's local wall clock (#590), so sidebar + calendar agree by
   construction rather than by sharing a tz."
  [date start-time]
  (when (and (blank->nil date) (blank->nil start-time))
    (let [[_y m d] (mapv #(js/parseInt % 10) (str/split date #"-"))
          [h mm] (mapv #(js/parseInt % 10) (str/split start-time #":"))
          ampm (if (>= h 12) "PM" "AM")
          h12 (let [x (mod h 12)] (if (zero? x) 12 x))]
      (str (nth month-abbrev (dec m)) " " d ", "
           h12 ":" (when (< mm 10) "0") mm " " ampm))))

(defn trial-when
  "Compact trial date/time for a row, or nil when no trial is scheduled.
   Prefers the active-trial-session wall clock (#590 — naive date + start-time);
   falls back to the legacy server-formatted :trial-datetime string (nearest
   booking, pre-Session model). The `tz` arg is kept for call-site
   compatibility, unused now that the value is already local."
  [{:keys [active-trial-session trial-datetime]} _tz]
  (or (format-session-when (:date active-trial-session)
                           (:start-time active-trial-session))
      (blank->nil trial-datetime)))

(defn program-name
  "The lead's program name, or nil. Reads the included :program relationship
   (GET /leads :list :include — SMS-35 Slice 2); falls back to a flat
   :program-name should the backend ever enrich it that way instead."
  [lead]
  (or (blank->nil (:name (:program lead)))
      (blank->nil (:program-name lead))))

(defn meta-line
  "Secondary context line under the row label: 'Program · Trial Jun 20, 1:00 PM'.
   Joins whichever parts are present (program name, then the trial) with ' · ',
   so it degrades gracefully when a lead has no program or no scheduled trial.
   Returns nil when neither is present (no line rendered)."
  [lead tz]
  (let [parts (->> [(program-name lead)
                    (when-let [w (trial-when lead tz)] (str "Trial " w))]
                   (filter some?))]
    (when (seq parts) (str/join " · " parts))))

;; ============================================================================
;; "Is this someone you already know?" (#767)
;; ============================================================================

(defn- humanize-state
  "\"trial-scheduled\" -> \"Trial Scheduled\". Only for rows the server did not
   FSM-enrich; a real :state-ui label always wins."
  [state]
  (when-let [st (blank->nil state)]
    (->> (str/split st #"-")
         (map str/capitalize)
         (str/join " "))))

(defn state-text
  "A lead's state as words: the server's FSM label, else a humanised raw state,
   else nil."
  [lead]
  (or (blank->nil (:label (state-ui lead)))
      (humanize-state (:state lead))))

(defn guardian-label
  "A guardian suggestion's name, which lives on the related Person."
  [{:keys [person]}]
  (or (blank->nil (str/trim (str (:first-name person) " " (:last-name person))))
      (blank->nil (:full-name person))
      "Unnamed guardian"))

(defn- fact [k v]
  (when-let [v (blank->nil (str v))] {:label k :value v}))

(defn details-facts
  "What tells one person from another once the name has already matched — the
   child, the phone, how they first reached us, and when we logged them.

   The search matched on NAME, so the name is the one thing that cannot
   disambiguate. Everything here is already on the record the suggestion was
   built from, so it costs nothing to show and saves opening the lead."
  [lead]
  (vec (keep identity
             [(fact "Child" (student-name lead))
              (fact "Program" (program-name lead))
              (fact "Phone" (:phone lead))
              (fact "Email" (:email lead))
              (fact "Source" (some-> (blank->nil (:lead-source lead))
                                     (str/replace "-" " ")))])))

(defn guardian-facts
  [{:keys [person]}]
  (vec (keep identity
             [(fact "Phone" (:phone person))
              (fact "Email" (:email person))])))

(defn suggestion-label
  "A lead's label in the suggestions panel: the PARENT, then the child.

   Not `primary-label`, which leads with the student. That is right in the
   Leads aside, where every row is already known to be this family's and the
   child is the only thing telling siblings apart. Here the question is \"who
   is messaging me\", the row sits directly beneath a Guardian row showing the
   parent's name, and answering with the child's made the same family read as
   two unrelated people.

   The child stays on the end, because two siblings' leads are still the case
   that has to be told apart. Same parent-then-child shape the lookup picker
   uses, so the two surfaces name a lead identically."
  [{:keys [display-name social-media-name first-name last-name] :as lead}]
  (let [parent (or (blank->nil display-name)
                   (blank->nil social-media-name)
                   (blank->nil (str/trim (str first-name " " last-name))))
        student (student-name lead)]
    (or (blank->nil (str/join " – " (remove nil? [parent student])))
        "Unnamed lead")))

(defn suggestions
  "View-model for the suggested-links panel — Leads and Guardians whose name
   matches the thread's sender.

   Takes the endpoint's payload verbatim. The server owns WHETHER to suggest at
   all (no guardian, no leads, not archived, usable sender-name), so this only
   has to decide how to say it: nil when there is nothing to offer, and the
   panel renders nothing.

   Every row carries what distinguishes one person from another — a lead's next
   action and state, a guardian's role — because the whole point is a decision
   the admin makes, and `Grace Lim` twice over is not a decision.

   `:more` is what the cap hid. Saying \"3 of 11\" rather than showing three
   silently is the difference between a suggestion and a claim about who
   exists."
  [payload school-tz]
  (when payload
    (let [{:keys [leads guardians leads-total guardians-total]} payload
          lead-rows (mapv (fn [l]
                            {:kind :lead
                             :id (:id l)
                             :label (suggestion-label l)
                             :state (:state l)
                             :state-ui (state-ui l)
                             ;; What KIND of record this is, always present.
                             ;; The guardian row said "Guardian" and the lead
                             ;; row said nothing, so a lead was identifiable
                             ;; only by not being labelled — and in the chip
                             ;; menu, which has no state badge, not at all.
                             ;;
                             ;; Carries the STATE too, falling back to a
                             ;; humanised raw state when the row was not
                             ;; FSM-enriched. That lets the row drop its
                             ;; separate state chip: name + chip + button on
                             ;; one 280px aside line truncated both the name
                             ;; and the chip, to say twice what this says once.
                             ;; Joined, never interpolated: `(str "Lead · " x)`
                             ;; with a stateless lead rendered "Lead · " —
                             ;; the same orphaned-separator bug the lookup
                             ;; label-template had.
                             :kind-label (str/join " · " (remove nil? ["Lead" (state-text l)]))
                             :subtitle (or (blank->nil (:card-subtitle l))
                                           (meta-line l school-tz))
                             ;; Identifying facts for the review step. Two
                             ;; families called Santos are separated by the
                             ;; child, the phone and how they first reached us —
                             ;; not by a name the search already matched on.
                             ;; Shown only there: the panel row has one line.
                             :facts (details-facts l)})
                          leads)
          guardian-rows (mapv (fn [g]
                                {:kind :guardian
                                 :id (:id g)
                                 :label (guardian-label g)
                                 :kind-label "Guardian"
                                 :subtitle nil
                                 :facts (guardian-facts g)})
                              guardians)]
      (when (or (seq lead-rows) (seq guardian-rows))
        ;; Guardians first: one means an ENROLLED family, which is a stronger
        ;; statement about who this is than a lead in the pipeline.
        {:rows (into guardian-rows lead-rows)
         :more (max 0 (+ (- (or leads-total 0) (count lead-rows))
                         (- (or guardians-total 0) (count guardian-rows))))}))))
